import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  MenuItem
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useUpdateProduct } from "../../hooks/api/useProduct";
import { useGetCategoryList } from "../../hooks/api/useCategory";
import { useGetSupplierList } from "../../hooks/api/useSupplier";
import { getLeafCategories } from "../Category/utils/categoryUtils";
import { Product, ProductFormData } from "./types/products";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  usePlatformConfig,
  resolvePlatformEnumCatalog
} from "@kivunova/kivufrontendcommon";
import {
  UNIT_DISPLAY_LABELS,
  SELLING_MODE_DISPLAY_LABELS
} from "./constants/displayLabels";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SellingMode } from "./enums/sellingMode";
import { Unit } from "./enums/unit";
import {
  getAllowedUnitsForSellingMode,
  getAutoSelectedUnit,
  isBaseUnitLocked,
  isPartialSellingPermitted
} from "./utils/sellingModeUtils";
import { buildProductFormSchema } from "./utils/buildProductFormSchema";
import { RootState } from "../../store/store";
import { CATEGORY_STATUS } from "../Category/enums/categoryEnums";
import FormModal, {
  formFieldSx,
  formSectionTitleSx
} from "../common/FormModal/FormModal";
import { useDispatch } from "react-redux";
import { resetSupplierList } from "../../store/slices/supplierSlice";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

interface EditProductModalProps {
  open: boolean;
  onClose: () => void;
  product: Product;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  open,
  onClose,
  product
}) => {
  const dispatch = useDispatch();
  const updateProduct = useUpdateProduct();
  const getCategoryList = useGetCategoryList();
  const { mutateAsync: fetchSupplierList, isPending: supplierListPending } =
    useGetSupplierList();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { sellingMode: platformSellingModes, baseUnit: platformBaseUnits } =
    usePlatformConfig();

  const allowedSellingModes = useMemo(
    () =>
      resolvePlatformEnumCatalog(
        platformSellingModes,
        Object.values(SellingMode)
      ),
    [platformSellingModes]
  );

  const allowedBaseUnits = useMemo(
    () => resolvePlatformEnumCatalog(platformBaseUnits, Object.values(Unit)),
    [platformBaseUnits]
  );

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const categoryList = useSelector(
    (state: RootState) => state.categories.categoryList
  );
  const supplierList = useSelector(
    (state: RootState) => state.suppliers.supplierList
  );

  // ── Schema ────────────────────────────────────────────────────────────────
  // Edit never requires storeId (it's already attached to the product).
  // variant: "edit" makes brandName and categoryId optional so legacy products
  // without those fields can still be saved.
  const validationSchema = useMemo(
    () =>
      buildProductFormSchema(t, {
        variant: "edit",
        requireStoreForCompanyAdmin: false,
        allowedSellingModes
      }),
    [t, allowedSellingModes]
  );

  // ── Form ──────────────────────────────────────────────────────────────────
  /**
   * The edit form maps the product's `category` string (persisted category id)
   * to `categoryId` so we can reuse the shared ProductFormData type and schema.
   */
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm<ProductFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      name: product.name ?? "",
      sellingMode: product.sellingMode,
      description: product.description ?? "",
      supplierId: product.supplierId ?? "",
      brandName: product.brandName ?? "",
      categoryId: product.category ?? "",
      baseUnit: product.baseUnit ?? "",
      isPartialSellingAllowed: product.isPartialSellingAllowed ?? false
    }
  });

  // ── Reset when product changes ────────────────────────────────────────────
  useEffect(() => {
    reset({
      name: product.name ?? "",
      sellingMode: product.sellingMode,
      description: product.description ?? "",
      supplierId: product.supplierId ?? "",
      brandName: product.brandName ?? "",
      categoryId: product.category ?? "",
      baseUnit: product.baseUnit ?? "",
      isPartialSellingAllowed: product.isPartialSellingAllowed ?? false
    });
  }, [product, reset]);

  // ── Load reference data on open ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    getCategoryList
      .mutateAsync({ status: CATEGORY_STATUS.ACTIVE })
      .catch(() => {});
    // Suppliers are scoped to the product's store; storeId may be absent for
    // legacy products:  gracefully do nothing when it's missing.
    if (product.storeId?.trim()) {
      fetchSupplierList({ storeId: product.storeId.trim() }).catch(() => {});
    } else {
      dispatch(resetSupplierList());
    }
  }, [open]);

  // ── Selling-mode change side-effects ──────────────────────────────────────
  const selectedSellingMode = watch("sellingMode") as SellingMode | undefined;

  /**
   * Guard against re-deriving unit/partial-selling on the initial render so
   * that the product's persisted values are not overwritten when the modal
   * first opens. The ref is reset whenever a different product is loaded.
   */
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    isFirstRender.current = true;
  }, [product.id]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const autoUnit = getAutoSelectedUnit(selectedSellingMode);
    if (autoUnit !== undefined) {
      setValue("baseUnit", autoUnit, { shouldValidate: true });
    } else {
      setValue("baseUnit", "", { shouldValidate: false });
    }

    if (!isPartialSellingPermitted(selectedSellingMode)) {
      setValue("isPartialSellingAllowed", false, { shouldValidate: false });
    }
  }, [selectedSellingMode, setValue]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit: SubmitHandler<ProductFormData> = async (data) => {
    // Log start of update
    sendAppLog(
      LOG_LEVEL.INFO,
      "Started updating product",
      buildLogContext(
        "products",
        "update-product-ui",
        userDetails?.id,
        userDetails?.companyId,
        userDetails?.storeId,
        { productId: product.id, productName: data.name }
      )
    );

    try {
      await updateProduct.mutateAsync({
        input: {
          id: product.id,
          name: data.name,
          description: (data.description ?? "").trim(),
          brandName: data.brandName?.trim(),
          supplierId: data.supplierId?.trim() || undefined,
          // The API field is `category` (category id string).
          category: data.categoryId?.trim() || undefined,
          sellingMode: data.sellingMode,
          baseUnit: data.baseUnit ?? undefined,
          isPartialSellingAllowed: data.isPartialSellingAllowed
        }
      });
      // Success is already logged inside the hook
      reset();
      onClose();
    } catch (err) {
      captureAppException(
        "Failed to update product",
        err,
        buildLogContext(
          "products",
          "update-product-ui",
          userDetails?.id,
          userDetails?.companyId,
          userDetails?.storeId,
          { productId: product.id }
        )
      );
    }
  };

  const isLoading = isSubmitting || updateProduct.isPending;
  const supplierPickerDisabled = isLoading || supplierListPending;

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderField = (
    name: keyof ProductFormData,
    label: string,
    placeholder?: string
  ) => {
    const isDescription = name === "description";

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            fullWidth
            size={matchMD ? "small" : "medium"}
            label={label}
            placeholder={placeholder}
            type="text"
            multiline={isDescription}
            rows={isDescription ? 3 : 1}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            disabled={isLoading}
            sx={formFieldSx}
            data-testid={`edit-product-field-${name}`}
          />
        )}
      />
    );
  };

  const renderSelectField = (
    name: keyof ProductFormData,
    label: string,
    options: { value: string | number; label: string }[]
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          fullWidth
          size={matchMD ? "small" : "medium"}
          label={label}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          disabled={isLoading}
          sx={formFieldSx}
          data-testid={`edit-product-field-${name}`}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );

  /**
   * Render a boolean field as a Yes/No select.
   * `locked` disables the field to enforce selling-mode constraints.
   */
  const renderBooleanField = (
    name: keyof ProductFormData,
    label: string,
    locked?: boolean
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          fullWidth
          size={matchMD ? "small" : "medium"}
          label={label}
          value={field.value ? "true" : "false"}
          onChange={(e) => field.onChange(e.target.value === "true")}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          disabled={isLoading || locked}
          sx={formFieldSx}
          data-testid={`edit-product-field-${name}`}
        >
          <MenuItem value="true">{t("yes", "Yes")}</MenuItem>
          <MenuItem value="false">{t("no", "No")}</MenuItem>
        </TextField>
      )}
    />
  );

  /**
   * Render the base unit select.
   * Available options and the locked state are derived from the current
   * sellingMode via sellingModeUtils so they stay consistent with the Add form.
   */
  const renderBaseUnitField = () => {
    const modeAllowed = getAllowedUnitsForSellingMode(selectedSellingMode);
    const allowedUnits = modeAllowed.filter((unit) =>
      allowedBaseUnits.includes(unit)
    );
    const locked = isBaseUnitLocked(selectedSellingMode);

    const options = allowedUnits.map((value) => ({
      value,
      label: UNIT_DISPLAY_LABELS[value]
    }));

    return (
      <Controller
        name="baseUnit"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            size={matchMD ? "small" : "medium"}
            label={t("edit-product-base-unit", "Base Unit*")}
            value={field.value ?? ""}
            error={!!errors.baseUnit}
            helperText={errors.baseUnit?.message}
            disabled={isLoading || locked}
            sx={formFieldSx}
            data-testid="edit-product-field-baseUnit"
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    );
  };

  const sellingModeOptions = allowedSellingModes
    .filter((value) =>
      Object.values(SellingMode).includes(value as SellingMode)
    )
    .map((value) => ({
      value,
      label: SELLING_MODE_DISPLAY_LABELS[value as SellingMode]
    }));

  const partialSellingLocked = !isPartialSellingPermitted(selectedSellingMode);

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={t("edit-product-title", "Edit Product")}
      dataTestId="edit-product-modal"
      icon={<EditRoundedIcon sx={{ color: BRAND_COLOR, fontSize: 28 }} />}
      footer={
        <>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover"
              }
            }}
          >
            {t("edit-product-cancel", "Cancel")}
          </Button>
          <LoadingButton
            form="edit-product-form"
            type="submit"
            variant="contained"
            loading={isLoading}
            size={matchMD ? "medium" : "large"}
            data-testid="edit-product-submit"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              backgroundColor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
            }}
          >
            {t("edit-product-update-product", "Update Product")}
          </LoadingButton>
        </>
      }
    >
      <Box
        component="form"
        id="edit-product-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      >
        {/* ── Required ────────────────────────────────────────────────────── */}
        <Typography component="span" sx={formSectionTitleSx}>
          {t("edit-product-section-required", "Required Information")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderField(
            "name",
            t("edit-product-product-name", "Product Name*"),
            t("edit-product-name-placeholder", "e.g. BoomHigh Speaker")
          )}
          {renderSelectField(
            "sellingMode",
            t("edit-product-selling-mode", "Selling Mode*"),
            sellingModeOptions
          )}
        </Box>

        {/* ── Additional ──────────────────────────────────────────────────── */}
        <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
          {t("edit-product-section-additional", "Additional Information")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "start"
          }}
        >
          {/* Supplier select — mirrors the Add modal */}
          <Controller
            name="supplierId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size={matchMD ? "small" : "medium"}
                label={t("edit-product-supplier", "Supplier")}
                value={field.value ?? ""}
                error={!!errors.supplierId}
                helperText={errors.supplierId?.message}
                disabled={supplierPickerDisabled}
                sx={formFieldSx}
                data-testid="edit-product-supplier-select"
              >
                <MenuItem value="">
                  {supplierListPending
                    ? t("edit-product-supplier-loading", "Loading suppliers…")
                    : t("edit-product-select-supplier", "Select supplier")}
                </MenuItem>
                {supplierList.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* Category select — uses categoryId field which maps to API `category` */}
          {renderSelectField(
            "categoryId",
            t("edit-product-category", "Category"),
            [
              {
                value: "",
                label: t("edit-product-select-category", "Select category")
              },
              ...getLeafCategories(categoryList).map((c) => ({
                value: c.id,
                label: c.name
              }))
            ]
          )}

          {renderBaseUnitField()}

          {renderField(
            "brandName",
            t("edit-product-brand-name", "Brand Name"),
            t("edit-product-brand-placeholder", "e.g. KivuNova")
          )}
        </Box>

        {/* ── Selling Configuration ────────────────────────────────────────── */}
        <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
          {t("edit-product-section-selling-config", "Selling Configuration")}
        </Typography>
        <Box sx={{ maxWidth: { sm: "50%" } }}>
          {renderBooleanField(
            "isPartialSellingAllowed",
            t("edit-product-allow-partial-selling", "Partial Selling Allowed*"),
            partialSellingLocked
          )}
        </Box>

        {/* ── Description ─────────────────────────────────────────────────── */}
        <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
          {t("edit-product-section-description", "Description")}
        </Typography>
        {renderField(
          "description",
          t("edit-product-description", "Description*"),
          t(
            "edit-product-description-placeholder",
            "Describe the product clearly and avoid special characters like < > { }"
          )
        )}
      </Box>
    </FormModal>
  );
};

export default EditProductModal;
